/**
 * <category-rail> — horizontal category navigator for collection pages.
 *
 * Consumes the theme's Category system (see sections/category-rail.liquid +
 * ADR-0008). Two data paths:
 *
 *   - metafield: cards are server-rendered; this element only wires interactions
 *     and loads each pill's product image.
 *   - json: builds the cards from the shared tree (assets/category-data.js): the
 *     current collection's children, or its siblings when it has none.
 *
 * Pill images are no longer this element's business under the METAFIELD source:
 * Liquid resolves every one through `collection.featured_image` (ADR-0016), so
 * those pills arrive painted and nothing here touches them.
 *
 * The JSON source has no collection object to ask — its tree is a flat file of
 * names and handles — so there, and only there, images load from
 * `/collections/<handle>/products.json` (sized down via the CDN). If the tree
 * handle has gone stale (the collection was re-handled and now 301s), the loader
 * resolves the current handle and retries; a pill with genuinely no product
 * image keeps its placeholder (and logs a hint in the theme editor) rather than
 * failing loudly — see `loadImage`.
 *
 * EMPTY CATEGORIES (`settings.category_show_empty` → `data-show-empty`, ADR-0015).
 * Only the JSON source filters here — metafield pills arrive pre-filtered from
 * Liquid, which has the real product counts. Two signals, in order:
 *   1. `products_count` in the tree, when the JSON carries it: authoritative and
 *      subtree-aware via `theme.categories.pruneEmpty`, applied before render.
 *   2. Otherwise the products.json probe the image loader already performs — a
 *      LEAF pill whose probe SUCCEEDS with zero products is a dead click and is
 *      hidden. A failed probe never hides anything, and a pill with
 *      subcategories is never judged on its own probe (an empty branch can
 *      still lead to stocked leaves).
 * A rail left with no visible pills collapses (`settleAfterProbes`).
 *
 * Reuses theme primitives: `initLazyScript` (defer until near-viewport),
 * `theme.utils.debounce`, the `image` snippet (SSR path), `icon-chevron-*`, and
 * `theme.BaseElement` (every listener auto-cleans on disconnect — ADR-0001).
 * Navigation is via the prev/next arrows and the keyboard (←/→/Home/End); touch
 * keeps native overflow scroll. Arrows disable at the track ends (the theme's
 * slider-nav convention).
 */

(function () {
  'use strict';

  // `escapeHtml` is required, not optional: this element builds card markup as
  // strings, and an escaper that silently degraded to identity would turn a
  // merchant-supplied category name into an injection vector. utils.js is a
  // <head> foundation script, so its absence means the page is broken anyway.
  if (!window.theme || !theme.BaseElement) return;
  if (!theme.utils || !theme.utils.escapeHtml) return;
  if (customElements.get('category-rail')) return;

  const cats = theme.categories || {};
  const escapeHtml = theme.utils.escapeHtml;
  const debounce = theme.utils.debounce || ((fn) => fn);
  const lazy = window.initLazyScript || ((el, cb) => cb());

  // First `images[0].src` across a products.json list — tolerates an image-less
  // lead product. Returns '' when none of the probed products carry an image.
  const firstImageSrc = (products) => {
    for (const p of products) {
      const src = p && p.images && p.images[0] && p.images[0].src;
      if (src) return src;
    }
    return '';
  };

  const SCROLL_THRESHOLD = 8; // px of slack before an arrow enables
  const SCROLL_RATIO = 0.75; // fraction of the viewport an arrow click scrolls
  const MAX_IMG_WIDTH = 600; // cap the requested CDN image width
  const PRODUCTS_PROBE = 4; // products to pull when hunting for a pill image

  // Theme-editor preview — surface "reached the collection but no image" hints
  // there only, so live visitors never see console noise for an intentionally
  // image-less (or not-yet-populated) collection.
  const inDesignMode = () => !!(window.Shopify && window.Shopify.designMode);

  class CategoryRail extends theme.BaseElement {
    constructor() {
      super();
      // Defer the whole init (and the image fetches it triggers) until the rail
      // is near the viewport — reuses the theme's lazy-init primitive.
      lazy(this, () => this.init());
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    init() {
      this.source = this.dataset.source || 'none';
      this.currentHandle = this.dataset.currentHandle || '';
      this.fallback = this.dataset.fallback || 'siblings';
      // Drop dead-end pills? Read the way compare reads its twin setting
      // (`data-compare-show-empty` in compare-modal.js): only an explicit "false"
      // filters, so a missing attribute can never remove a category. Scoped to the
      // JSON source — metafield pills arrive already filtered from Liquid, which
      // is the side that has the real product counts.
      this.dropEmpty = this.dataset.showEmpty === 'false' && this.source === 'json';
      this.cardSize = parseInt(this.dataset.cardSize, 10) || 80;
      this.rtl = document.documentElement.dir === 'rtl';

      this.track = this.querySelector('[data-category-rail-track]');
      this.prevBtn = this.querySelector('[data-category-rail-prev]');
      this.nextBtn = this.querySelector('[data-category-rail-next]');
      if (!this.track) return;

      this.rafId = null;

      if (this.source === 'json') {
        this.buildFromTree();
      } else {
        this.activate();
      }
    }

    /* ---------- JSON source: build cards from the shared tree ----------- */

    async buildFromTree() {
      if (!cats.load || !cats.findChildrenOrSiblings) return;
      try {
        // Which categories to show — children, else siblings, each filtered
        // before the choice is made — is pure tree work and lives in the shared
        // data layer. This element only turns the answer into DOM.
        const tree = await cats.load();
        const nodes = cats.findChildrenOrSiblings(tree, this.currentHandle, {
          fallback: this.fallback,
          dropEmpty: this.dropEmpty,
        });

        if (!nodes) {
          this.hidden = true; // nothing to show — collapse (see .category-rail[hidden])
          return;
        }

        this.track.innerHTML = Object.entries(nodes)
          .filter(([, sub]) => sub && sub.handle)
          .map(([name, sub]) => this.cardHTML(name, sub))
          .join('');

        this.activate();
      } catch {
        this.hidden = true;
      }
    }

    cardHTML(name, node) {
      const handle = node.handle;
      // A leaf can be judged empty by its own products.json probe; a branch
      // cannot (its stocked leaves would go with it), so only leaves are marked.
      const leaf = !node.subcategories || !Object.keys(node.subcategories).length;
      return (
        '<a href="/collections/' +
        escapeHtml(handle) +
        '" class="category-rail__card" data-handle="' +
        escapeHtml(handle) +
        '"' +
        (leaf ? ' data-leaf' : '') +
        '>' +
        '<span class="category-rail__media" aria-hidden="true"><span class="category-rail__placeholder"></span></span>' +
        '<span class="category-rail__title">' +
        escapeHtml(name) +
        '</span></a>'
      );
    }

    /* ---------- Images: first product image, sized down via the CDN ----- */

    loadImages() {
      // JSON source only. Under the metafield source Liquid has already asked
      // `featured_image` — which covers the collection's own image AND the first
      // product's — so a placeholder there means there is genuinely no image to
      // find, and probing would only reintroduce the per-pill request ADR-0016
      // removed (and which 401s on a password-protected store anyway).
      if (this.source !== 'json') return;

      const cards = Array.from(this.track.querySelectorAll('[data-handle]')).filter((card) => card.querySelector('.category-rail__placeholder'));
      const probes = cards.map((card) => this.loadImage(card));
      // Empty-leaf pruning rides on those same probes, so the rail can only
      // settle its arrows — or collapse — once they have all answered.
      // `loadImage` swallows its own failures, so this never rejects.
      if (this.dropEmpty) Promise.all(probes).then(() => this.settleAfterProbes());
    }

    // Re-measure once the async probes have had their say: a rail whose every
    // pill turned out to be a dead end disappears instead of leaving an empty
    // track and a heading behind.
    settleAfterProbes() {
      if (!this.isConnected) return;
      const visible = Array.from(this.track.children).some((el) => !el.hidden);
      if (!visible) {
        this.hidden = true;
        return;
      }
      this.updateArrows();
    }

    async loadImage(card) {
      const handle = card.dataset.handle;
      const media = card.querySelector('.category-rail__media');
      const placeholder = media && media.querySelector('.category-rail__placeholder');
      if (!handle || !media || !placeholder) return;
      try {
        let products = await this.fetchProducts(handle);
        let src = firstImageSrc(products || []);

        // Stale-handle recovery. When a collection is re-handled in admin,
        // Shopify keeps a 301 from the old handle — but products.json on the old
        // handle answers 200 with an EMPTY list (it doesn't follow the redirect
        // the way the HTML page does). The shared category tree can lag behind
        // such renames, so an empty probe isn't proof the category is empty:
        // resolve the collection's CURRENT handle from its redirecting page and
        // retry once, re-pointing the pill at the live handle as we go.
        if (!src && (!products || !products.length)) {
          const live = await this.resolveHandle(handle);
          if (live && live !== handle) {
            this.retarget(card, live);
            products = await this.fetchProducts(live);
            src = firstImageSrc(products || []);
          }
        }

        // Dead click: this leaf has no products. Only a SUCCESSFUL probe proves
        // that — a failed one (`null`) must never remove a category — and only
        // a leaf is judged this way (see `cardHTML`).
        if (this.dropEmpty && card.hasAttribute('data-leaf') && products && products.length === 0) {
          card.hidden = true;
          return;
        }

        if (!src) {
          // Genuinely nothing to show: an empty collection, one not published to
          // the Online Store sales channel (products.json lists only Online-
          // Store-published products), or a dead handle. The fix is the
          // merchant's (populate / publish / re-handle), not the code's — so it
          // only hints in the editor and the pill keeps its placeholder.
          let reason;
          if (!products) reason = 'products.json did not answer (non-OK response or malformed body)';
          else if (products.length) reason = products.length + ' product(s) but none carry an image';
          else reason = 'no products from products.json — empty, unpublished from the Online Store channel, or a dead handle';
          this.hintBlank(handle, reason);
          return;
        }

        // Request a pill-sized image (2× for retina) instead of the full-res
        // product photo — a meaningful payload saving for an 80px circle.
        const width = Math.min(MAX_IMG_WIDTH, this.cardSize * 2);
        const sized = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;

        // Swap the placeholder for the image immediately — the browser decodes
        // and paints it in place (no `load`-event gating, a past failure point).
        const img = document.createElement('img');
        img.className = 'category-rail__img';
        img.alt = '';
        img.width = this.cardSize;
        img.height = this.cardSize;
        img.decoding = 'async';
        img.loading = 'lazy';
        img.src = sized;
        placeholder.replaceWith(img);
      } catch (err) {
        // AbortError on disconnect is expected; anything else is a real failure
        // worth surfacing (network drop, malformed JSON) — keep the placeholder.
        if (err && err.name !== 'AbortError') console.warn('[category-rail] image load failed for collection "' + handle + '":', err);
      }
    }

    // First few products of a collection. Returns an array (possibly empty) when
    // the collection ANSWERED, and `null` when the probe itself failed (404,
    // any non-OK). The two must not be conflated: "answered with none" is proof
    // a category is empty and may hide it, "did not answer" is proof of nothing.
    async fetchProducts(handle) {
      const res = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PRODUCTS_PROBE, { signal: this._abort.signal });
      if (!res.ok) return null;
      const data = await res.json();
      return (data && data.products) || [];
    }

    // Resolve a collection's CURRENT handle by following its page redirect with
    // a body-less HEAD (an old handle 301s to the new one after a re-handle).
    // A still-valid handle resolves back to itself; returns '' if undeterminable.
    async resolveHandle(handle) {
      try {
        const res = await fetch('/collections/' + encodeURIComponent(handle), { method: 'HEAD', signal: this._abort.signal });
        const m = res.url && res.url.match(/\/collections\/([^/?#]+)/);
        return m ? decodeURIComponent(m[1]) : '';
      } catch {
        return '';
      }
    }

    // Re-point a pill at a collection's live handle so the image probe — and a
    // click — hit it directly instead of bouncing through Shopify's 301.
    retarget(card, handle) {
      card.dataset.handle = handle;
      if (card.tagName === 'A') card.setAttribute('href', '/collections/' + handle);
    }

    // A pill stayed blank for a known, merchant-fixable reason (empty/unpublished
    // collection, dead handle). Surface it only in the theme editor so live
    // visitors get no console noise for an intentionally image-less collection.
    hintBlank(handle, reason) {
      if (inDesignMode()) console.warn('[category-rail] collection "' + handle + '" — ' + reason);
    }

    /* ---------- Shared interactions (both sources) --------------------- */

    activate() {
      if (!this.track.children.length) {
        this.hidden = true;
        return;
      }
      this.wireInteractions();
      this.loadImages();
      requestAnimationFrame(() => this.updateArrows());
    }

    wireInteractions() {
      this.on(this.track, 'scroll', () => this.scheduleArrowUpdate(), { passive: true });
      this.on(
        window,
        'resize',
        debounce(() => this.updateArrows(), 150),
        { passive: true },
      );
      this.on(this.track, 'keydown', (e) => this.onKeydown(e));

      if (this.prevBtn) this.on(this.prevBtn, 'click', () => this.scrollByDir(-1));
      if (this.nextBtn) this.on(this.nextBtn, 'click', () => this.scrollByDir(1));
    }

    scheduleArrowUpdate() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.updateArrows());
    }

    // Arrows DISABLE at the track ends (the theme's slider-nav convention) —
    // disabled is accessible (out of tab order) and the CSS fades them out.
    updateArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      const { scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      const pos = Math.abs(this.track.scrollLeft); // abs() tolerates RTL negatives
      if (this.prevBtn) this.prevBtn.disabled = pos <= SCROLL_THRESHOLD;
      if (this.nextBtn) this.nextBtn.disabled = !(max > 0 && pos < max - SCROLL_THRESHOLD);
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    // direction: -1 = previous, +1 = next. Flipped for RTL so the chevrons match
    // the scroll direction (the track's scrollLeft runs negative in RTL).
    scrollByDir(direction) {
      const amount = this.track.clientWidth * SCROLL_RATIO * direction * (this.rtl ? -1 : 1);
      this.track.scrollBy({ left: amount, behavior: this.scrollBehavior() });
    }

    onKeydown(e) {
      if (e.target !== this.track) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.scrollByDir(this.rtl ? 1 : -1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.scrollByDir(this.rtl ? -1 : 1);
          break;
        case 'Home':
          e.preventDefault();
          this.track.scrollTo({ left: 0, behavior: this.scrollBehavior() });
          break;
        case 'End':
          e.preventDefault();
          this.track.scrollTo({ left: this.rtl ? -this.track.scrollWidth : this.track.scrollWidth, behavior: this.scrollBehavior() });
          break;
      }
    }
  }

  customElements.define('category-rail', CategoryRail);
})();
